import { HttpClient } from '@angular/common/http';
import { EventEmitter, Injectable, Injector } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';
import { map } from 'rxjs/operators';
import { createHttpParams } from '~/infrastructure/http/params';
import { augmentPaging } from '~/infrastructure/paging';
import { AddressSummary } from '~/models/modules/address';
import { CustomerListing, CustomersListingWithEntityAddressDto, CustomerSummary } from '~/models/modules/customer';
import { ApiServiceBase, API_NAMES } from '~/services/api';

@Injectable({ providedIn: 'root' })

export class CustomersService extends ApiServiceBase {
  customerClosed: Subject<string> = new Subject<string>();
  customerAdded: EventEmitter<any> = new EventEmitter();
  customerUpdated: EventEmitter<any> = new EventEmitter();

  constructor(injector: Injector, private http: HttpClient) {
    super(injector);
    this.setBase(API_NAMES.stocks);
  }

  getCustomer(id: number) {
    return this.http.get<CustomerSummary>(this._url(`/${id}`));
  }

  getCustomerShipToAddresses(customerId: number) {
    return this.http.get<AddressSummary[]>(this._url(`/${customerId}/addresses`));
  }

  getCustomersListing(paramsObj?: any, screenSiteId?: number) {
    paramsObj = paramsObj || {};
    paramsObj.screenSiteId = screenSiteId;
    let params = createHttpParams(paramsObj);

    return this.http.get<CustomerListing[]>(this._url(), { params });
  }

  getCustomersListingBySite(siteId: number) {
    return this.http.get<CustomerListing[]>(this._url(`/site/${siteId}`));
  }

  GetIssuedItemsCustomersBySite(siteId: number) {
    return this.http.get<CustomerListing[]>(this._url(`/IssuedItemsCustomersBySite/${siteId}`));
  }

  getCustomersBySiteId(siteIds: number[]) {
    return this.http.post<CustomerListing[]>(this._url(`/site`), siteIds);
  }

  getCustomers(paramsObj?: any, model?: any, screenSiteId?: number) {
    paramsObj = paramsObj || {};
    paramsObj.view = 'summary';
    paramsObj.screenSiteId = screenSiteId;
    let params = createHttpParams(paramsObj);

    return this.http.post<CustomerSummary[]>(this._url(`/grid-data`), model, { params, observe: 'response' }).pipe(map(augmentPaging));
  }

  CheckIfCustomerHasApprovalCycle(customerId: number, siteId?: number) {
    return this.http.get(this._url(`/Customers/${customerId}/Sites/${siteId}/ApprovalCycles`));
  }

  getAccountCustomerTypes(model: any) {
    return this.http.post<any>(this._url(`/customertypesaccounts`), model);
  }

  getAccountCustomers(model: any) {
    return this.http.post<any>(this._url(`/accounts`), model);
  }

  getCustomersListingWithEntityAddress() {
    return this.http.get<CustomersListingWithEntityAddressDto[]>(this._url(`/withentityaddress`));
  }

  getCustomerWithEntityAddress(id: number) {
    return this.http.get<CustomersListingWithEntityAddressDto>(this._url(`/withentityaddress/${id}`));
  }

  createCustomer(model: any) {
    return this.client.post<string>(this._url(), model);
  }

  updateCustomer(id: number, model: any) {
    return this.client.put(this._url(`/${id}`), model);
  }

  deleteCustomer(id: number) {
    return this.client.delete(this._url(`/${id}`));
  }

  deactivateCustomer(obj: any) {
    return this.client.put(this._url(`/deactivate`), obj);
  }

  deactivateCutemerBatch(obj: any) {
    return this.client.put(this._url(`/batchDeactivate`), obj);
  }

  reactivateCustomer(obj: any) {
    return this.client.put(this._url(`/reactivate`), obj);
  }

  reactivateCutemerBatch(obj: any) {
    return this.client.put(this._url(`/reactivate-batch`), obj);
  }

  getSONumberPerDate(id: number) {
    return this.client.get(this._url(`/so-per-date/${id}`));
  }

  getIssuedItemPerDate(id: number) {
    return this.client.get(this._url(`/issue-by-category/${id}`));
  }

  getIssuedItemsPerstockitemIdPerDate(id: number) {
    return this.client.get(this._url(`/issue-by-stock-id/${id}`));
  }

  getBackordersPerCustomer(id: number) {
    return this.client.get(this._url(`/back-orders/${id}`));
  }

  getIssueTransactionsPerCustomer(id: number) {
    return this.client.get(this._url(`/issue-transaction-for-customer/${id}`));
  }

  GetNumberOfSoByCustomer(customerId: number) {
    return this.client.get(this._url(`/Customers/${customerId}/NumberOfSOChart`));
  }

  GetIssueItemsByCategoryByCustomer(customerId: number) {
    return this.client.get(this._url(`/Customers/${customerId}/IssuedItemsByCategory`));
  }

  GetIssueItemsByStockNumberByCustomer(customerId: number) {
    return this.client.get(this._url(`/Customers/${customerId}/IssuedItemsByStockNumber`));
  }

  GetNumberOfSoBackOrderByCustomer(customerId: number) {
    return this.client.get(this._url(`/Customers/${customerId}/SoBackOrder`));
  }

  GetTransactionHistoryByCustomer(customerId: number) {
    return this.client.get(this._url(`/Customers/${customerId}/TransactionHistory`));
  }

  private _url(v?: string | undefined) {
    v = v || '';
    return this.url(`/Customers${v}`);
  }

  customerFormClosed(newCustomer: any) {
    this.customerClosed.next(newCustomer);
  }
}
